/**
 * Spartan Fitness — Header Nearest Store Location Widget & Dropdown
 * Computes closest retail showroom using HTML5 Geolocation or IP fallback,
 * provides manual dropdown store switcher, and caches user preference in localStorage.
 */

(function () {
  let metaobjectStores = [];
  try {
    const metaTag = document.getElementById('SpartanMetaobjectStoresData');
    if (metaTag && metaTag.textContent.trim()) {
      const parsed = JSON.parse(metaTag.textContent);
      if (Array.isArray(parsed) && parsed.length > 0) {
        metaobjectStores = parsed.filter(s => s && s.name && s.city);
      }
    }
  } catch (e) {
    console.warn('[Spartan Stores] Metaobjects parse error:', e);
  }

  const SPARTAN_STORES = (function() {
    if (!metaobjectStores || metaobjectStores.length === 0) return [];
    return metaobjectStores.map(s => {
      const tz = provinceTimezone(s.province);

      // Normalize website path for Kelowna, Calgary, and London manual pages
      let web = s.website;
      const cleanCity = s.city ? s.city.toLowerCase().trim() : "";
      if (cleanCity === "kelowna") {
        web = "/pages/kelownapickup";
      } else if (cleanCity === "london") {
        web = "/pages/london-on";
      } else if (cleanCity === "calgary") {
        web = "/pages/calgary";
      }

      return {
        ...s,
        name: s.city + (s.province ? ", " + s.province : ""),
        website: web,
        timezone: tz
      };
    }).sort((a, b) => {
      // Canadian showroom list runs geographically west to east.
      const aLng = parseFloat(a.lng);
      const bLng = parseFloat(b.lng);
      if (Number.isNaN(aLng) && Number.isNaN(bLng)) return a.name.localeCompare(b.name);
      if (Number.isNaN(aLng)) return 1;
      if (Number.isNaN(bLng)) return -1;
      return aLng - bLng;
    });
  })();

  const STORAGE_KEY = "spartan_nearest_store_override";

  function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function findClosestStore(lat, lng) {
    let closestStore = null;
    let minDistance = Infinity;

    SPARTAN_STORES.forEach(function (store) {
      const sLat = parseFloat(store.lat) || 0;
      const sLng = parseFloat(store.lng) || 0;
      if (!sLat || !sLng) return;
      const dist = haversineDistance(lat, lng, sLat, sLng);
      if (!isNaN(dist) && dist < minDistance) {
        minDistance = dist;
        closestStore = Object.assign({}, store, { distance: dist });
      }
    });

    return closestStore || SPARTAN_STORES[0];
  }

  // Mobile carriers often route BC phones through Alberta IPs (and similar), so an
  // IP lookup can land a Vancouver shopper in Edmonton. The phone's own time zone is
  // reliable: if the IP pick is in a different time zone, use the closest store that
  // shares the shopper's time zone instead.
  function browserTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (e) {
      return "";
    }
  }

  function checkAgainstTimezone(store, lat, lng) {
    const tz = browserTimezone();
    if (!store || !tz || store.timezone === tz) return store;
    if (!SPARTAN_STORES.some((s) => s.timezone === tz)) return store;
    let best = null;
    SPARTAN_STORES.forEach(function (s) {
      if (s.timezone !== tz || !parseFloat(s.lat) || !parseFloat(s.lng)) return;
      const d = haversineDistance(lat, lng, parseFloat(s.lat), parseFloat(s.lng));
      if (!best || d < best.distance) best = Object.assign({}, s, { distance: d });
    });
    // Distance is only shown for GPS locations (store.precise), never IP guesses.
    return best || store;
  }

  function parseTime(timeStr) {
    if (!timeStr) return null;
    const match = timeStr.trim().match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    return hours + minutes / 60;
  }

  function getStoreStatusText(store) {
    if (!store.schedule || !store.timezone) {
      return { isOpen: true, text: " Open Now" };
    }
    try {
      const storeLocalString = new Date().toLocaleString("en-US", { timeZone: store.timezone });
      const storeLocalDate = new Date(storeLocalString);
      const day = storeLocalDate.getDay(); // 0 = Sun, 1 = Mon, etc.
      const currentHours = storeLocalDate.getHours() + storeLocalDate.getMinutes() / 60;

      const hoursSetting = store.schedule[day];
      const isClosedToday = !hoursSetting || hoursSetting.toLowerCase().includes("closed") || hoursSetting.toLowerCase().includes("coming soon");

      if (hoursSetting && hoursSetting.toLowerCase().includes("coming soon")) {
        return { isOpen: false, text: " Showroom Opening Soon" };
      }

      let todayOpenTime = null;
      let todayCloseTime = null;
      let todayOpenStr = "";
      let todayCloseStr = "";

      if (!isClosedToday) {
        const parts = hoursSetting.split("-");
        if (parts.length === 2) {
          todayOpenStr = parts[0].trim();
          todayCloseStr = parts[1].trim();
          todayOpenTime = parseTime(todayOpenStr);
          todayCloseTime = parseTime(todayCloseStr);
        }
      }

      if (!isClosedToday && todayOpenTime !== null && todayCloseTime !== null && currentHours >= todayOpenTime && currentHours < todayCloseTime) {
        return {
          isOpen: true,
          text: `Open Now • Closes ${todayCloseStr}`
        };
      }

      if (!isClosedToday && todayOpenTime !== null && currentHours < todayOpenTime) {
        return {
          isOpen: false,
          text: `Closed • Opens today ${todayOpenStr}`
        };
      }

      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      for (let i = 1; i <= 7; i++) {
        const nextDay = (day + i) % 7;
        const nextDaySetting = store.schedule[nextDay];
        if (nextDaySetting && !nextDaySetting.toLowerCase().includes("closed") && !nextDaySetting.toLowerCase().includes("coming soon")) {
          const parts = nextDaySetting.split("-");
          if (parts.length === 2) {
            const nextOpenStr = parts[0].trim();
            const dayLabel = i === 1 ? "tomorrow" : dayNames[nextDay];
            return {
              isOpen: false,
              text: `Closed • Opens ${dayLabel} ${nextOpenStr}`
            };
          }
        }
      }

      return { isOpen: false, text: "Closed" };
    } catch (e) {
      return { isOpen: true, text: " Open Now" };
    }
  }

  class SpartanNearestStoreWidget {
    constructor() {
      this.widget = document.getElementById("spartanNearestStoreWidget");
      if (!this.widget) return;

      this.trigger = this.widget.querySelector(".spartan-nearest-store-trigger");
      this.triggerText = this.widget.querySelector(".spartan-nearest-store-text");
      this.dropdown = this.widget.querySelector(".spartan-nearest-store-dropdown");
      this.selectEl = document.getElementById("spartanStoreSelect");
      this.detectBtn = document.getElementById("spartanDetectLocationBtn");

      this.currentStore = null;
      this.isOverride = false;
      this.stores = SPARTAN_STORES;

      this.init();
    }

    init() {
      this.populateSelectOptions();
      this.bindEvents();
      this.determineStore();
    }

    populateSelectOptions() {
      if (!this.selectEl) return;
      this.selectEl.innerHTML = '<option value="">Select your store...</option>';
      SPARTAN_STORES.forEach((store) => {
        const opt = document.createElement("option");
        opt.value = store.id;
        opt.textContent = store.name;
        this.selectEl.appendChild(opt);
      });
    }

    bindEvents() {
      if (this.widget) {
        this.widget.addEventListener("click", (e) => {
          const trigger = e.target.closest(".spartan-nearest-store-trigger");
          if (trigger) {
            e.preventDefault();
            e.stopPropagation();
            this.toggleDropdown();
          }
        });
      }

      document.addEventListener("click", (e) => {
        if (this.widget && !this.widget.contains(e.target)) {
          this.closeDropdown();
        }
      });

      if (this.selectEl) {
        this.selectEl.addEventListener("change", (e) => {
          const selectedId = e.target.value;
          if (selectedId) {
            this.setManualOverride(selectedId);
          }
        });
      }

      if (this.detectBtn) {
        this.detectBtn.addEventListener("click", () => {
          try {
            localStorage.removeItem(STORAGE_KEY);
          } catch (e) {}
          this.isOverride = false;
          this.detectUserLocation();
        });
      }
    }

    toggleDropdown() {
      const isOpen = this.widget.classList.contains("spartan-open");
      if (isOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    openDropdown() {
      this.widget.classList.add("spartan-open");
      if (this.trigger) {
        this.trigger.setAttribute("aria-expanded", "true");
      }
      if (this.dropdown) {
        this.dropdown.setAttribute("aria-hidden", "false");
      }
    }

    closeDropdown() {
      this.widget.classList.remove("spartan-open");
      if (this.trigger) {
        this.trigger.setAttribute("aria-expanded", "false");
      }
      if (this.dropdown) {
        this.dropdown.setAttribute("aria-hidden", "true");
      }
    }

    determineStore() {
      let savedId = null;
      try {
        savedId = localStorage.getItem(STORAGE_KEY);
      } catch (e) {}

      if (savedId) {
        const matched = SPARTAN_STORES.find((s) => s.id === savedId);
        if (matched) {
          this.isOverride = true;
          this.renderStore(matched);
          return;
        }
      }

      this.detectUserLocation();
    }

    detectUserLocation() {
      if (SPARTAN_STORES.length > 0) {
        this.renderStore(SPARTAN_STORES[0]);
      }

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const lat = pos.coords.latitude;
            const lng = pos.coords.longitude;
            const closest = Object.assign({}, findClosestStore(lat, lng), { precise: true });
            this.renderStore(closest);
          },
          (err) => {
            this.ipFallback();
          },
          { timeout: 6000, maximumAge: 300000 }
        );
      } else {
        this.ipFallback();
      }
    }

    ipFallback() {
      // geojs is free with no rate limit; ipapi.co (1k/day free) is the backup.
      const lookup = () =>
        fetch("https://get.geojs.io/v1/ip/geo.json")
          .then((res) => res.json())
          .then((d) => ({ lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) }))
          .then((p) => { if (!p.lat || !p.lng) throw new Error("no geo"); return p; })
          .catch(() =>
            fetch("https://ipapi.co/json/")
              .then((res) => res.json())
              .then((d) => ({ lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) }))
          );

      lookup()
        .then((p) => {
          if (p && p.lat && p.lng) {
            this.renderStore(checkAgainstTimezone(findClosestStore(p.lat, p.lng), p.lat, p.lng));
          } else {
            this.renderTimezoneGuess();
          }
        })
        .catch(() => this.renderTimezoneGuess());
    }

    // No IP location at all: pick the first store in the shopper's own time zone.
    renderTimezoneGuess() {
      const tz = browserTimezone();
      const match = tz && SPARTAN_STORES.find((s) => s.timezone === tz);
      this.renderStore(match || SPARTAN_STORES[0]);
    }

    setManualOverride(storeId) {
      const matched = SPARTAN_STORES.find((s) => s.id === storeId);
      if (!matched) return;

      try {
        localStorage.setItem(STORAGE_KEY, storeId);
      } catch (e) {}

      this.isOverride = true;
      this.renderStore(matched);
    }

    renderStore(store) {
      if (!store) store = SPARTAN_STORES[0];
      if (!store) return;
      this.currentStore = store;

      this.widget.style.display = "inline-block";

      let triggerLabel = `<span class="spartan-store-prefix">${this.isOverride ? "Your Store: " : "Nearest Store: "}</span><span class="spartan-store-location">${store.city}</span>`;
      if (!this.isOverride && store.precise && store.distance !== undefined && store.distance !== null) {
        triggerLabel = `<span class="spartan-store-prefix">Nearest Store: </span><span class="spartan-store-location">${store.city} <span class="spartan-store-distance-badge">(${Math.round(store.distance)} km)</span></span>`;
      }
      if (this.triggerText) {
        this.triggerText.innerHTML = triggerLabel;
      }

      if (!this.isOverride && store.precise && store.distance !== undefined && store.distance < 25) {
        this.widget.classList.add("spartan-pulse-attention");
      } else {
        this.widget.classList.remove("spartan-pulse-attention");
      }

      const storeNameEl = this.widget.querySelector(".spartan-store-name");
      if (storeNameEl) storeNameEl.textContent = `Spartan Fitness ${store.name}`;

      const storeAddrEl = this.widget.querySelector(".spartan-store-address");
      if (storeAddrEl) storeAddrEl.textContent = `${store.address}, ${store.city}, ${store.province} ${store.postal}`;

      const storePhoneEl = this.widget.querySelector(".spartan-store-phone");
      if (storePhoneEl) {
        if (store.phone) {
          storePhoneEl.style.display = "block";
          storePhoneEl.innerHTML = `📞 <a href="tel:${store.phone}">${store.phone}</a>`;
        } else {
          storePhoneEl.style.display = "none";
        }
      }

      const status = getStoreStatusText(store);
      const liveIndicator = this.widget.querySelector(".spartan-live-indicator");
      if (liveIndicator) {
        liveIndicator.className = "spartan-live-indicator " + (status.isOpen ? "spartan-open-status" : "spartan-closed-status");
        liveIndicator.innerHTML = `<span class="spartan-live-dot"></span> ${status.text}`;
      }

      const dirBtn = this.widget.querySelector(".spartan-btn-directions");
      if (dirBtn) {
        dirBtn.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Spartan Fitness " + store.address + " " + store.city + " " + store.province)}`;
      }

      const pageBtn = this.widget.querySelector(".spartan-btn-page");
      if (pageBtn) {
        pageBtn.href = store.website || "/pages/locations";
      }

      if (this.selectEl) {
        this.selectEl.value = store.id;
      }

      this.currentDistance = store.distance;
      document.dispatchEvent(new CustomEvent("spartan:store_changed", {
        detail: {
          store: store,
          distance: store.distance
        }
      }));

      if (!this.isOverride && store.precise && store.distance !== undefined && store.distance <= 20) {
        checkAndShowGlassmorphismBubble(store);
      }
    }
  }

  const BUBBLE_STORAGE_KEY = "spartan_nearest_store_bubble_timestamp";
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

  function checkAndShowGlassmorphismBubble(store) {
    if (!store || store.distance === undefined || store.distance === null || store.distance > 20) {
      return;
    }

    try {
      const lastShown = localStorage.getItem(BUBBLE_STORAGE_KEY);
      if (lastShown) {
        const timePassed = Date.now() - parseInt(lastShown, 10);
        if (timePassed < THIRTY_DAYS_MS) {
          return;
        }
      }
    } catch (e) {}

    try {
      localStorage.setItem(BUBBLE_STORAGE_KEY, Date.now().toString());
    } catch (e) {}

    const distFormatted = Math.round(store.distance);
    let bubble = document.getElementById("spartanGlassBubble");
    if (!bubble) {
      bubble = document.createElement("div");
      bubble.id = "spartanGlassBubble";
      bubble.className = "spartan-glass-bubble";
      document.body.appendChild(bubble);
    }

    bubble.innerHTML = `
      <div class="spartan-glass-bubble-content">
        <span class="spartan-glass-bubble-icon">📍</span>
        <span class="spartan-glass-bubble-text">Nearest store is <strong>${store.city}</strong> (${distFormatted}km)</span>
        <button type="button" class="spartan-glass-bubble-close" aria-label="Close">&times;</button>
      </div>
    `;

    setTimeout(() => {
      bubble.classList.add("spartan-bubble-visible");
    }, 1500);

    bubble.addEventListener("click", (e) => {
      if (e.target.classList.contains("spartan-glass-bubble-close")) {
        e.stopPropagation();
        bubble.classList.remove("spartan-bubble-visible");
        return;
      }
      const widget = document.getElementById("spartanNearestStoreWidget");
      if (widget) {
        const trigger = widget.querySelector(".spartan-nearest-store-trigger");
        if (trigger) trigger.click();
      }
      bubble.classList.remove("spartan-bubble-visible");
    });

    setTimeout(() => {
      if (bubble && bubble.classList.contains("spartan-bubble-visible")) {
        bubble.classList.remove("spartan-bubble-visible");
      }
    }, 11500);
  }

  // Hoisted, so the SPARTAN_STORES initialiser above can already call it.
  function provinceTimezone(province) {
    const prov = province ? province.trim().toUpperCase() : "";
    if (prov === "BC") return "America/Vancouver";
    if (prov === "AB") return "America/Edmonton";
    if (prov === "SK") return "America/Regina";
    if (prov === "MB") return "America/Winnipeg";
    if (prov === "NL") return "America/St_Johns";
    if (["NS", "PE", "NB"].includes(prov)) return "America/Halifax";
    return "America/Toronto";
  }

  /**
   * Shared with the PDP's nearest-hub card, which carries its own store JSON
   * and cannot reach inside this IIFE. Takes the raw schedule and a province
   * so the caller never repeats the timezone table, and returns the same
   * { isOpen, text } shape the header widget renders.
   *
   * Returns null when the status cannot be computed. Callers must treat null
   * as "show nothing" -- the bug this replaced was a badge that asserted
   * "Open Now" whether or not anything had checked.
   */
  window.spartanStoreStatus = function (schedule, province) {
    if (!schedule) return null;
    const status = getStoreStatusText({
      schedule: schedule,
      timezone: provinceTimezone(province)
    });
    // getStoreStatusText falls back to a bare "Open Now" when it cannot read
    // the schedule. That fallback is the thing we are removing, so reject it.
    if (!status || status.text.trim() === "Open Now") return null;
    return status;
  };

  function initSpartanNearestStore() {
    window.spartanStoreWidgetInstance = new SpartanNearestStoreWidget();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSpartanNearestStore);
  } else {
    initSpartanNearestStore();
  }
})();
